import { Language } from '@boltenergy-be/shared'
import { TFunction } from 'i18next'
import { DEFAULT_FILE_SIZE_LIMIT } from 'constants/constants'
import { formatFileSize } from '@boltenergy-be/design-system'

/**
 * Returns the indexes of files in the given FileList that are empty or exceed the size limit
 *
 * @param {FileList | null} fileList
 * @param {number} limit - In bytes, defaults to 3 MB
 * @returns {number[]}
 */
export const getInvalidFileIndexes = (fileList: FileList | null, limit: number = DEFAULT_FILE_SIZE_LIMIT): number[] => {
  if (!fileList) return []

  return Array.from(fileList)
    .map((file, index) => (file.size === 0 || file.size > limit ? index : -1))
    .filter((index) => index !== -1)
}

/**
 * Validates that the number of files in the given FileList does not exceed the maximum allowed amount
 *
 * @param {FileList} fileList
 * @param {number} maxAmountFiles
 * @param {TFunction} t
 * @returns {boolean | string}
 */
export const validateFileAmount = (fileList: FileList, maxAmountFiles: number, t: TFunction): boolean | string => {
  if (fileList && fileList.length > maxAmountFiles) {
    return t('validation:max.amountOfFiles', { max: maxAmountFiles })
  }

  return true
}

/**
 * Validates that every file in the given FileList is non-empty and within the size limit
 *
 * @description This is in line with the bodyParser sizeLimit of the API function. Upload limit is technically 4mb, but we have to account for a 33% size increase on formatting to base64. Thus to ensure we stay under 4mb post formatting, the default limit is 3 MiB (3 * 1024 * 1024 bytes). (File: src/pages/api/customers/index.ts)
 * @param {FileList} fileList
 * @param {number} limit - In bytes, defaults to 3 MB (3 * 1024 * 1024)
 * @param {Language} locale
 * @param {TFunction} t
 * @returns {boolean | string}
 */
export const validateFileSize = (
  fileList: FileList,
  limit: number = DEFAULT_FILE_SIZE_LIMIT,
  locale: Language,
  t: TFunction
): boolean | string => {
  if (fileList && Array.from(fileList).some((file) => file.size === 0)) {
    return t('validation:emptyFile')
  }

  const invalidIndexes = getInvalidFileIndexes(fileList, limit)

  if (invalidIndexes.length > 0) {
    return t('validation:max.file', { max: formatFileSize(limit, locale) })
  }

  return true
}
